import type { CardStandard, LessonStandard } from "@shared/models/card-stack"
import { mergeAttributes, Node } from "@tiptap/core"
import type { DOMOutputSpec, Node as PMNode } from "@tiptap/pm/model"
import type { Transaction } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { renderJsonAsData } from "../../util/render-data"
import { StandardComponent } from "./standard-component"

/**
 * Collect every standard currently in the lesson, deduplicated by id (a standard can appear
 * in more than one card).
 */
export function extractLessonStandards(doc: PMNode): Array<LessonStandard> {
  const standards: Array<LessonStandard> = []
  const seenIds = new Set<string>()
  doc.descendants((node) => {
    if (node.type.name === "standard") {
      const { standard } = node.attrs as ExtensionStandardAttrs
      if (!seenIds.has(standard.id)) {
        seenIds.add(standard.id)
        standards.push(standard)
      }
    }
  })
  return standards
}

/**
 * A command that deletes every standard node whose standard id is in `ids` from the lesson.
 *
 * Positions are found against the transaction's own document rather than the editor's, so this
 * can be chained AFTER an insertion in the same transaction — which is what lets one standards
 * edit (the adds and the removals together) be a single mutation, and so carry a single event.
 */
export function deleteStandardsByIdsCommand(ids: ReadonlySet<string>) {
  return ({ tr }: { tr: Transaction }): boolean => {
    if (ids.size === 0) return true

    const positions: Array<number> = []
    tr.doc.descendants((node, pos) => {
      if (
        node.type.name === "standard" &&
        ids.has((node.attrs as ExtensionStandardAttrs).standard.id)
      ) {
        positions.push(pos)
      }
    })

    // Delete from last to first so earlier positions stay valid as we go.
    for (let i = positions.length - 1; i >= 0; i--) {
      const pos = positions[i]
      const node = tr.doc.nodeAt(pos)
      if (node) tr.delete(pos, pos + node.nodeSize)
    }
    return true
  }
}

/** The statement's plain text, with any markup stripped. */
export function statementText(html: string): string {
  return new DOMParser().parseFromString(html, "text/html").body.textContent ?? ""
}

export interface ExtensionStandardAttrs {
  standard: LessonStandard
  /** STARRED is EXPANDED plus a star. */
  emphasisLevel: CardStandard["emphasisLevel"]
}

type EmphasisLevel = CardStandard["emphasisLevel"]

/**
 * Where a click on the row leaves it: open rows close, closed rows open. A starred standard is
 * already open and stays starred — the level it returns is the one it was given, which is how the
 * row's handler knows there is nothing to commit.
 */
export function emphasisAfterRowClick(level: EmphasisLevel): EmphasisLevel {
  if (level === "STARRED") return "STARRED"
  return level === "EXPANDED" ? null : "EXPANDED"
}

/** Where a click on the star leaves it. Unstarring closes the row: STARRED is EXPANDED plus a star. */
export function emphasisAfterStarClick(level: EmphasisLevel): EmphasisLevel {
  return level === "STARRED" ? null : "STARRED"
}

export const ExtensionStandard = Node.create({
  name: "standard",
  group: "block widget",
  draggable: true,

  addAttributes() {
    return {
      standard: {
        isRequired: true,
        ...renderJsonAsData("standard"),
      },
      // Absent for null rather than empty, so the CSS's `data-emphasis-level:` reads as expanded.
      emphasisLevel: {
        default: null,
        renderHTML: (attributes) => {
          const level = (attributes as ExtensionStandardAttrs).emphasisLevel
          return level === null ? {} : { "data-emphasis-level": level }
        },
        parseHTML: (element) => element.getAttribute("data-emphasis-level"),
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    const { standard, emphasisLevel } = node.attrs as ExtensionStandardAttrs

    // renderHTML has two uses: rendering a nice read-only view of the standard
    // for the legacy cards array, and serializing for the clipboard (inverted by parseHTML).
    // A glyph rather than the NodeView's star icon: a DOMOutputSpec array builds its elements
    // with createElement, so an <svg> written here would come out as an inert HTML element.
    const star: Array<DOMOutputSpec> =
      emphasisLevel === "STARRED" ? [["span", { class: "standard-star" }, "★ "]] : []
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-content-type": "standard",
        class: "standard-row",
      }),
      ...star,
      // Guard against null DOMOutputSpec entries, even though the types allow them.
      // They cause error `Uncaught TypeError: can't access property "nodeType", structure is null`.
      ["span", { class: "standard-code" }, standard.code ?? ""],
      ["span", {}, standard.statement ?? ""],
      [
        "span",
        { class: "standard-set-info" },
        `${standard.jurisdiction}. ${standard.subject}. ${standard.grades}`,
      ],
    ]
  },

  // Plain-text rendering (getText and the clipboard's text/plain flavor); without
  // this, a standard contributes nothing since all its data lives in attrs.
  renderText({ node }) {
    const { standard, emphasisLevel } = node.attrs as ExtensionStandardAttrs
    const star = emphasisLevel === "STARRED" ? "★ " : ""
    return `${star}${standard.code ?? ""} ${statementText(standard.statement)} (${standard.jurisdiction}. ${standard.subject}. ${standard.grades}.)`
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-content-type="standard"]',
      },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(StandardComponent)
  },
})
